import { Box, FormControl, FormHelperText, Stack, Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { ErrorMessage, Form, Formik, FormikProps } from 'formik';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { useGetLegalDocumentsQuery } from '../../../../api/legal-documents';
import { LegalDocument } from '../../../../domain/config';
import { RequiredText } from '../../../../general/components/required-text';
import { WizardPage } from '../../../../general/components/wizard-page';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { controlPersonAttestation } from '../../../application/components/personal-info-form/complianceAttestations';
import { nextStep, selectnextStep } from '../../../application/reducers/next-step';
import { OtherIndividuals } from '../../../application-review/components/other-individuals';
import { selectOtherIndividuals } from '../../../application-review/reducers/other-individuals.reducer';
import { DocumentLinks } from '../../../legal-consent/components/document-links';
import { recordConsent, selectConsent } from '../../../legal-consent/reducers/consent';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { selectIsSubaccount } from '../../reducers/is-subaccount';

const errorMessage = 'Your submission must include at least one individual with significant management responsibility.';

const selectAll = createSelector(
  [selectOtherIndividuals, selectnextStep, selectConsent, selectIsSubaccount],
  (otherIndividuals, nextStep, consent, isSubaccount) => ({
    otherIndividuals,
    saving: consent.loading || nextStep.loading,
    isSubaccount: isSubaccount.isSubaccount,
  }),
);

const VerifyUbosPage: FC = () => {
  const { otherIndividuals, saving, isSubaccount } = useSelector(selectAll);
  const { data: legalDocs } = useGetLegalDocumentsQuery();
  const legalDocuments = (
    isSubaccount
      ? [
          legalDocs?.entities.card_program_terms ?? null,
          legalDocs?.entities.privacy_policy ?? null,
          legalDocs?.entities.platform_terms ?? null,
        ]
      : [legalDocs?.entities.platform_terms ?? null, legalDocs?.entities.privacy_policy ?? null]
  ).filter((doc) => doc !== null);

  const validationSchema = yup.object({
    ...(isSubaccount && {
      controlPersonAttestation: yup
        .mixed()
        .oneOf(
          [true],
          'You must confirm that you have significant management responsibility and are authorized to submit this application.',
        ),
    }),
    consentCardTerms: yup.mixed().oneOf([true], `You must read and accept the terms`),
  });
  const dispatch = useDispatch();

  const isOtherIndividualControlPerson = useMemo(() => {
    return otherIndividuals.some((individual) => individual.controlPerson);
  }, [otherIndividuals]);

  const validate = useCallback(() => {
    return !isOtherIndividualControlPerson ? { hasControlPerson: errorMessage } : {};
  }, [isOtherIndividualControlPerson]);

  return (
    <WizardPage title="Tell us about your people">
      <Formik
        initialValues={{
          consentCardTerms: false,
          hasControlPerson: false,
          controlPersonAttestation: false,
          isOtherIndividualControlPerson: false,
        }}
        validationSchema={validationSchema}
        onSubmit={() => {
          dispatch(
            recordConsent({
              documentKeys: legalDocuments.map(({ key }) => key),
              onSuccess: [
                nextStep({
                  action: 'verify_ubos',
                  gotoNextPage: true,
                }),
              ],
            }),
          );
        }}
        validate={validate}
      >
        {(formik) => {
          return (
            <VerifyUbosForm
              formik={formik}
              legalDocuments={legalDocuments}
              isSubaccount={isSubaccount}
              saving={saving}
              isOtherIndividualControlPerson={isOtherIndividualControlPerson}
            />
          );
        }}
      </Formik>
    </WizardPage>
  );
};

type VerifyUbosFormProps = {
  formik: FormikProps<{
    consentCardTerms: boolean;
    hasControlPerson: boolean;
    controlPersonAttestation: boolean;
    isOtherIndividualControlPerson: boolean;
  }>;
  legalDocuments: LegalDocument[];
  isSubaccount: boolean;
  saving: boolean;
  isOtherIndividualControlPerson: boolean;
};

const VerifyUbosForm: FC<VerifyUbosFormProps> = ({
  formik,
  legalDocuments,
  isSubaccount,
  saving,
  isOtherIndividualControlPerson,
}) => {
  useEffect(() => {
    formik
      .setFieldValue('isOtherIndividualControlPerson', isOtherIndividualControlPerson)
      .catch((err) => console.log(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOtherIndividualControlPerson]);

  return (
    <Form>
      <Stack>
        <OtherIndividuals showApplicant={false} includePhone={false} />
        <FormikToggle
          formik={formik}
          id="consentCardTerms"
          name="consentCardTerms"
          label={
            <RequiredText>
              I agree to the Coast <DocumentLinks documents={legalDocuments} />
            </RequiredText>
          }
          toggleStyle="checkbox"
        />
        {isSubaccount && (
          <FormikToggle
            name="controlPersonAttestation"
            formik={formik}
            id="controlPersonAttestation"
            label={
              <RequiredText>
                {controlPersonAttestation} and I am authorized to submit this application on behalf of the business.
              </RequiredText>
            }
            toggleStyle="checkbox"
          />
        )}
        <Box>
          <Typography variant="body2">
            By submitting, I attest that the information provided is accurate to the best of my knowledge.
          </Typography>
        </Box>

        <ErrorMessage name="hasControlPerson">{(msg) => <ControlPersonError message={msg} />}</ErrorMessage>
        <SubmitWorkflowButton type="submit" sx={{ mt: 4 }} progress={saving}>
          Submit
        </SubmitWorkflowButton>
      </Stack>
    </Form>
  );
};

const ControlPersonError: FC<{ message: string }> = ({ message }) => (
  <FormControl error>
    <FormHelperText>{message}</FormHelperText>
  </FormControl>
);

export { VerifyUbosPage };
